import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Eye,
  EyeOff,
  LockKeyhole,
  Mail,
  UsersRound,
} from "lucide-react";
import Button from "../components/Button";
import { Blob, Dots, Star } from "../components/Decor";
import AccentRays from "../components/decor/AccentRays";
import HandDrawnUnderline from "../components/decor/HandDrawnUnderline";
import { startDemoSession } from "../lib/demoAuth";
import brandLogo from "../assets/images/logo-fix.png";
import SEO from "../components/SEO";

const inputClass =
  "min-h-[52px] w-full rounded-[14px] border border-ink/15 bg-white py-3 pl-12 pr-4 text-sm font-medium text-ink outline-none transition placeholder:text-ink/35 focus:border-purple focus:ring-4 focus:ring-purple/10";

function PageDecor() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <Dots className="absolute left-4 top-10 h-20 w-20 opacity-35 sm:left-10" color="#73DCC6" />
      <Star className="absolute right-[8%] top-16 h-7 w-7 opacity-75" color="#6554E8" />
      <Star className="absolute left-[11%] top-[42%] hidden h-4 w-4 opacity-55 lg:block" color="#FFD966" />
      <Blob className="absolute -bottom-28 -left-24 h-72 w-72 opacity-20" color="#BEB5FF" />
      <svg
        viewBox="0 0 170 70"
        className="absolute -bottom-1 right-3 h-20 w-44 opacity-70 sm:right-10"
        fill="none"
        focusable="false"
      >
        <path
          d="M5 55c28-3 33-32 50-31 11 .5 5 22-6 20-12-2-3-27 13-24 14 3 10 28-2 31-9 2-13-17 2-22 23-8 30 29 56 13 12-7 20-18 26-31"
          stroke="#E7B928"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = {};

    if (!/^\S+@\S+\.\S+$/.test(email)) {
      nextErrors.email = "Masukkan alamat email yang valid.";
    }
    if (!password) {
      nextErrors.password = "Kata sandi perlu diisi.";
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length === 0) {
      let parentName = "Pengguna";
      try {
        const profile = JSON.parse(localStorage.getItem("msm_parent_profile"));
        parentName = profile?.parentName?.trim() || "Pengguna";
      } catch {
        /* Use the safe fallback name. */
      }
      startDemoSession({ role: "parent", name: parentName, email, remember: rememberMe });
      navigate("/dashboard/orang-tua", { replace: true });
    }
  };

  return (
    <main className="relative overflow-hidden min-h-screen bg-warm px-3 py-4 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
      <SEO title="Masuk | MySchoolMate" canonicalPath="/login" noIndex />
      <PageDecor />
      <div className="relative z-10 mx-auto grid min-h-[780px] max-w-[1320px] overflow-hidden rounded-[30px] border border-ink/10 bg-white shadow-soft lg:grid-cols-[0.55fr_0.45fr] lg:rounded-[34px]">
        <section className="flex flex-col px-6 py-8 sm:px-10 sm:py-10 lg:px-12 xl:px-16 xl:py-12">
          <Link to="/" className="w-fit rounded-lg focus-ring" aria-label="Kembali ke beranda MySchoolMate">
            <img
              src={brandLogo}
              alt="MySchoolMate"
              width="288"
              height="43"
              className="h-8 w-auto sm:h-9"
            />
          </Link>

          <div className="relative mt-10 sm:mt-12">
            <AccentRays
              variant="triple"
              color="yellow"
              rotation={18}
              className="absolute -right-1 -top-6 h-9 w-9 sm:right-8"
            />
            <h1 className="max-w-[520px] text-[2.25rem] font-extrabold leading-[1.08] text-ink sm:text-[2.75rem] lg:text-[3rem] xl:text-[3.35rem]">
              Masuk dan lanjutkan{" "}
              <span className="relative inline-block">
                perjalanan Anda
                <HandDrawnUnderline
                  variant="swoosh"
                  color="mint"
                  className="absolute -bottom-3 left-0 h-4 w-full"
                />
              </span>
            </h1>
          </div>

          <p className="mt-6 max-w-[520px] text-[15px] font-medium leading-relaxed text-ink/65 sm:text-base">
            Masuk untuk melanjutkan pencarian, perbandingan, dan perjalanan menemukan sekolah yang sesuai untuk keluarga Anda.
          </p>

          <div className="mt-5 inline-flex w-fit items-center gap-2 rounded-full border border-purple/15 bg-purple/10 px-3.5 py-2.5 text-xs font-bold text-ink sm:text-sm">
            <UsersRound className="h-4 w-4 shrink-0 text-purple" aria-hidden="true" />
            Untuk orang tua / wali &amp; perwakilan sekolah
          </div>

          <form onSubmit={handleSubmit} noValidate className="mt-7 max-w-[520px]">
            <div>
              <label htmlFor="login-email" className="mb-2 block text-sm font-bold text-ink">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-ink/45" aria-hidden="true" />
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setErrors((current) => ({ ...current, email: undefined }));
                  }}
                  placeholder="Masukkan email Anda"
                  autoComplete="email"
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? "login-email-error" : undefined}
                  className={inputClass}
                />
              </div>
              {errors.email ? (
                <p id="login-email-error" className="mt-1.5 text-xs font-semibold text-coral">
                  {errors.email}
                </p>
              ) : null}
            </div>

            <div className="mt-5">
              <label htmlFor="login-password" className="mb-2 block text-sm font-bold text-ink">
                Kata sandi
              </label>
              <div className="relative">
                <LockKeyhole className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-ink/45" aria-hidden="true" />
                <input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    setErrors((current) => ({ ...current, password: undefined }));
                  }}
                  placeholder="Masukkan kata sandi Anda"
                  autoComplete="current-password"
                  aria-invalid={Boolean(errors.password)}
                  aria-describedby={errors.password ? "login-password-error" : undefined}
                  className={`${inputClass} pr-12`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg text-ink/50 hover:bg-cream focus-ring"
                  aria-label={showPassword ? "Sembunyikan kata sandi" : "Tampilkan kata sandi"}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {errors.password ? (
                <p id="login-password-error" className="mt-1.5 text-xs font-semibold text-coral">
                  {errors.password}
                </p>
              ) : null}
            </div>

            <div className="mt-4 flex items-center justify-between gap-4">
              <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-ink/70">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(event) => setRememberMe(event.target.checked)}
                  className="h-4 w-4 rounded accent-purple"
                />
                Ingat saya
              </label>
              <span
                className="cursor-not-allowed text-sm font-semibold text-ink/40"
                aria-disabled="true"
                title="Pemulihan kata sandi belum tersedia"
              >
                Lupa kata sandi?
              </span>
            </div>

            <Button type="submit" variant="primary" className="mt-6 min-h-[54px] w-full rounded-[14px] text-white">
              Masuk
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </Button>

            <p className="mt-7 flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-center text-sm font-medium text-ink/60">
              Belum punya akun?
              <Link to="/signup" className="inline-flex items-center gap-1 font-bold text-purple underline-offset-4 hover:underline focus-ring">
                Pilih jenis akun
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </p>
          </form>
        </section>

        <section className="flex min-h-[440px] items-center justify-center bg-[#FCFBFF] p-4 sm:min-h-[520px] sm:p-8 lg:min-h-[780px] lg:p-8 xl:p-10 lg:order-first">
          <img
            src="/signin.png"
            alt="Ilustrasi keluarga dan siswa menggunakan MySchoolMate untuk mencari dan membandingkan sekolah"
            width="1122"
            height="1402"
            className="h-full max-h-[760px] w-full object-contain rounded-3xl"
          />
        </section>
      </div>
    </main>
  );
}
